import { LEAVES_BG_XS, LEAVES_BG_MD } from '../config/backgrounds';
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Rating,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  CircularProgress,
  Snackbar,
  IconButton,
  Link,
  Stack,
  Collapse,
  Breadcrumbs
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  LocationOn,
  Phone,
  Email,
  Star,
  Verified,
  Schedule,
  Euro,
  Message,
  Share,
  ArrowBack,
  Person,
  Event,
  RateReview,
  PhotoCamera,
  Close,
  ContentCopy,
  ZoomIn,
  ThumbUp,
  ThumbDown,
  FormatListBulleted,
  Reply as ReplyIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { primaryCounty } from '../constants/counties';
import { categoryLandingPath, providersLandingPath, countyLabel } from '../utils/landingPaths';
import { useAuth } from '../contexts/AuthContext';
import WriteReview from '../components/Reviews/WriteReview';
import OfferRequestForm from '../components/Booking/OfferRequestForm';
import FavoriteButton from '../components/Favorites/FavoriteButton';
import providerService from '../services/providerService';
import { trackProviderView, trackProviderEvent, trackGallerySwipe, ANALYTICS_EVENTS } from '../services/analyticsService';
import reviewService from '../services/reviewService';
import messageService from '../services/messageService';
import MapComponent from '../components/Map/MapComponent';
import ImageGallery from '../components/ImageGallery/ImageGallery';
import ImageLightbox from '../components/ImageGallery/ImageLightbox';
import PackageList from '../components/Provider/PackageList';

const FacebookIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
  </svg>
);

const InstagramIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="url(#ig-gradient)">
    <defs>
      <linearGradient id="ig-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#f09433"/>
        <stop offset="25%" stopColor="#e6683c"/>
        <stop offset="50%" stopColor="#dc2743"/>
        <stop offset="75%" stopColor="#cc2366"/>
        <stop offset="100%" stopColor="#bc1888"/>
      </linearGradient>
    </defs>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#000000">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V9.05a8.17 8.17 0 004.79 1.54V7.14a4.85 4.85 0 01-1.02-.45z"/>
  </svg>
);

const WebsiteIcon = ({ size = 20, color = '#2a5d32' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
  </svg>
);

const ensureAbsoluteUrl = (url) => {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const ServiceProviderDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isClient, isProvider } = useAuth();
  const reviewsRef = useRef(null);
  const theme = useTheme();
  
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactInfoDialogOpen, setContactInfoDialogOpen] = useState(false);
  const [writeReviewOpen, setWriteReviewOpen] = useState(false);
  
  const [contactForm, setContactForm] = useState({
    name: user ? `${user.firstName} ${user.lastName}` : '',
    email: user ? user.email : '',
    phone: user ? user.phone || '' : '',
    eventDate: '',
    message: ''
  });

  useEffect(() => {
    if (user) {
      setContactForm(prev => ({
        ...prev,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        phone: user.phone || ''
      }));
    }
  }, [user]);

  const [submitStatus, setSubmitStatus] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [offerRequestDialogOpen, setOfferRequestDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [contactType, setContactType] = useState('');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [visibleReviewCount, setVisibleReviewCount] = useState(3);
  const [highlightedReviewId, setHighlightedReviewId] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyError, setReplyError] = useState(null);

  // Térkép állapotok
  const [mapCenter, setMapCenter] = useState(null);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [nearbyProviders, setNearbyProviders] = useState([]);
  const [mapZoomDialogOpen, setMapZoomDialogOpen] = useState(false);

  const profileEnterTimeRef = useRef(null);
  const sessionIdRef = useRef((() => {
    // sessionStorage-ban perzisztálva, hogy oldal-újratöltés esetén is ugyanaz a session-azonosító
    // maradjon (a backend PROFILE_VIEW dedup-ja ezen az ID-n alapul).
    try {
      const existing = sessionStorage.getItem('wedora_session_id');
      if (existing) return existing;
    } catch (e) {
      // sessionStorage nem elérhető (pl. privát böngészés) - csak memóriában tartjuk
    }
    const newId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString(36) + Math.random().toString(36).slice(2);
    try {
      sessionStorage.setItem('wedora_session_id', newId);
    } catch (e) {
      // ignorálható
    }
    return newId;
  })());
  const [contactRevealed, setContactRevealed] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);

  // Értékelések betöltése (numerikus provider ID-val)
  const loadReviews = useCallback(async (numericProviderId) => {
    try {
      const response = await reviewService.getReviewsForProvider(numericProviderId);
      if (response.success) {
        setReviews(response.data.reviews);
      }
    } catch (err) {
      console.error('Értékelések betöltése hiba:', err);
    }
  }, []);

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (location.state?.scrollToReviews && reviewsRef.current) {
      setTimeout(() => {
        reviewsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [location.state?.scrollToReviews]);

  // Mélylink egy konkrét értékeléshez (pl. "Új értékelés érkezett" értesítésből):
  // #review-<id> hash. Ha az adott értékelés a lapozás miatt még nincs a
  // látható listában, előbb kibővítjük, majd a következő rendernél görgetünk
  // hozzá és röviden kiemeljük — ugyanaz a minta, mint a fórum válaszoknál.
  useEffect(() => {
    if (!location.hash?.startsWith('#review-')) return;
    const targetReviewId = location.hash.replace('#review-', '');
    // A render alján számolt filteredApprovedReviews itt még nem biztos, hogy
    // elérhető (a loading/error/!provider korai return-ök miatt ez a hook
    // előbb fut le, mint az a const) — ezért ugyanazt a szűrést a nyers
    // reviews/reviewFilter state-ekből, közvetlenül itt végezzük el.
    const approved = reviews.filter(r => r.status === 'APPROVED');
    const filtered = approved.filter(r => {
      if (reviewFilter === 'good') return r.rating >= 4;
      if (reviewFilter === 'bad') return r.rating <= 3;
      return true;
    });
    const idx = filtered.findIndex(r => String(r.id) === targetReviewId);
    if (idx === -1) return;
    if (idx >= visibleReviewCount) {
      setVisibleReviewCount(idx + 1);
      return;
    }
    const el = document.getElementById(`review-${targetReviewId}`);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // React state-en keresztül állítjuk a kiemelő hátteret (nem közvetlen
        // DOM-mutációval), mert egy soron következő re-render (pl. profil
        // megtekintés-számláló frissítése) egyébként visszaállítaná a Box
        // sx-ben definiált stílusára.
        setHighlightedReviewId(targetReviewId);
        setTimeout(() => setHighlightedReviewId(null), 2000);
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, reviews, reviewFilter, visibleReviewCount]);

  // Profilon töltött idő mérése
  useEffect(() => {
    if (!provider) return;
    profileEnterTimeRef.current = Date.now();
    return () => {
      const seconds = Math.round((Date.now() - profileEnterTimeRef.current) / 1000);
      if (seconds >= 5) {
        trackProviderEvent(ANALYTICS_EVENTS.TIME_ON_PROFILE, provider.id, { seconds });
      }
    };
  }, [provider]);

  useEffect(() => {
    let cancelled = false;

    const loadProviderDetails = async () => {
      if (slug === '__preview__') {
        const raw = sessionStorage.getItem('providerPreviewData');
        if (!cancelled) {
          if (raw) {
            setProvider(JSON.parse(raw));
          } else {
            setError('Előnézeti adatok nem találhatók. Kérlek, nyisd meg az előnézetet a profil szerkesztő oldalról.');
          }
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setProvider(null);
        setNearbyProviders([]);
        setMapMarkers([]);

        const response = await providerService.getProviderDetails(slug);
        if (cancelled) return;

        if (response.success) {
          const fetchedProvider = response.data.provider;
          setProvider(fetchedProvider);
          loadReviews(fetchedProvider.id);
          trackProviderView(fetchedProvider.slug || fetchedProvider.id, fetchedProvider.category?.name);
          trackProviderEvent(ANALYTICS_EVENTS.PROFILE_VIEW, fetchedProvider.id, { sessionId: sessionIdRef.current });

          // Térkép adatok beállítása
          if (fetchedProvider.latitude && fetchedProvider.longitude) {
            const centerCoords = {
              lat: fetchedProvider.latitude,
              lng: fetchedProvider.longitude
            };
            setMapCenter(centerCoords);

            const currentProviderMarker = {
              id: fetchedProvider.id,
              lat: fetchedProvider.latitude,
              lng: fetchedProvider.longitude,
              title: fetchedProvider.businessName,
              description: fetchedProvider.city,
              type: 'current',
              link: `/szolgaltato/${fetchedProvider.slug || fetchedProvider.id}`,
              icon: {
                url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
                scaledSize: { width: 32, height: 32 }
              }
            };
            let markers = [currentProviderMarker];

            try {
              const nearbyResponse = await providerService.getNearbyProviders(
                fetchedProvider.latitude,
                fetchedProvider.longitude,
                500,
                { limit: 200 }
              );
              if (!cancelled && nearbyResponse.success && nearbyResponse.data.providers.length > 0) {
                const filteredNearby = nearbyResponse.data.providers.filter(
                  (p) => p.id !== fetchedProvider.id
                );
                setNearbyProviders(filteredNearby);
                const nearbyMarkers = filteredNearby.map((p) => ({
                  id: p.id,
                  lat: p.latitude,
                  lng: p.longitude,
                  title: p.businessName,
                  description: p.city,
                  type: 'nearby',
                  link: `/szolgaltato/${p.slug || p.id}`,
                  avatarUrl: p.userAvatarUrl,
                  categoryName: p.categoryName,
                  categoryIcon: p.categoryIcon,
                  icon: {
                    url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                    scaledSize: { width: 32, height: 32 }
                  }
                }));
                markers = [...markers, ...nearbyMarkers];
              }
            } catch (nearbyError) {
              console.warn('Közeli szolgáltatók lekérése hiba:', nearbyError);
            }
            if (!cancelled) setMapMarkers(markers);
          }

        } else {
          throw new Error(response.message || 'Szolgáltató betöltése sikertelen');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Szolgáltató betöltése hiba:', err);
          setError('Szolgáltató betöltése sikertelen.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProviderDetails();

    return () => { cancelled = true; };
  }, [slug, loadReviews]);

  const handleContactSubmit = async () => {
    if (!isAuthenticated) {
      setSubmitStatus({ type: 'error', message: 'Az üzenet küldéséhez be kell jelentkeznie!' });
      return;
    }
    if (!provider?.userId) {
      setSubmitStatus({ type: 'error', message: 'A szolgáltató felhasználói azonosítója hiányzik.' });
      return;
    }
    if (!contactForm.message.trim()) {
      setSubmitStatus({ type: 'error', message: 'Az üzenet mező nem lehet üres.' });
      return;
    }

    try {
      setSubmitStatus({ type: 'loading', message: 'Üzenet küldése...' });

      const recipientId = provider.userId;
      let messageContent = `Üzenet a MyMarketplace-ről:\n\n`;
      messageContent += `Feladó neve: ${contactForm.name || 'Nincs megadva'}\n`;
      messageContent += `Feladó email címe: ${contactForm.email || 'Nincs megadva'}\n`;
      messageContent += `Feladó telefonszáma: ${contactForm.phone || 'Nincs megadva'}\n`;
      if (contactForm.eventDate) {
        messageContent += `Esemény dátuma: ${contactForm.eventDate}\n`;
      }
      messageContent += `Üzenet: ${contactForm.message}\n`;

      const response = await messageService.sendMessage(recipientId, messageContent);

      if (response.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Üzenet sikeresen elküldve! A szolgáltató hamarosan válaszol.'
        });
        setTimeout(() => {
          setContactDialogOpen(false);
          setSubmitStatus(null);
          setContactForm({
            name: user ? `${user.firstName} ${user.lastName}` : '',
            email: user ? user.email : '',
            phone: user ? user.phone || '' : '',
            eventDate: '',
            message: ''
          });
        }, 2000);
      } else {
        throw new Error(response.message || 'Üzenet küldése sikertelen.');
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Hiba történt az üzenet küldése során!'
      });
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setSnackbarMessage('Link vágólapra másolva!');
    setSnackbarOpen(true);
  };

  const handleReviewSubmitted = (newReview) => {
    if (provider) loadReviews(provider.id);
    setWriteReviewOpen(false);
  };

  const canWriteReview = () => {
    if (!isAuthenticated || !isClient()) return false;
    
    const hasReviewed = reviews.some(review => 
      review.clientId === user.id && review.providerId === provider.id
    );
    
    return !hasReviewed;
  };

  const getReviewButtonText = () => {
    if (!isAuthenticated) return 'Bejelentkezés szükséges';
    if (!isClient()) return 'Csak ügyfelek írhatnak értékelést';
    
    const hasReviewed = reviews.some(review => 
      review.clientId === user.id && review.providerId === provider.id
    );
    
    return hasReviewed ? 'Már írt értékelést' : 'Értékelés írása';
  };

  const handleOfferRequestSuccess = (newBooking) => {
    console.log('Offer request created:', newBooking);
    setOfferRequestDialogOpen(false);
    setSnackbarMessage('Az ajánlatkérés sikeresen elküldve! A szolgáltatót értesítettük az ajánlatkérési igényéről.');
    setSnackbarOpen(true);
  };

  const handleOpenContactInfoDialog = (method) => {
    setContactType(method);
    setContactInfoDialogOpen(true);
  };

  const handleCopyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage(`${type} másolva a vágólapra!`);
      setSnackbarOpen(true);
      setContactInfoDialogOpen(false);
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      setSnackbarMessage('Másolás sikertelen');
      setSnackbarOpen(true);
    }
  };

  const handleMapMarkerClick = (markerId) => {
    const clickedProvider = nearbyProviders.find(p => p.id === markerId);
    if (clickedProvider) {
      navigate(`/szolgaltato/${clickedProvider.slug || clickedProvider.id}`);
    }
  };

  const handleCityChipClick = (city) => {
    navigate(`/szolgaltatok?city=${city}`);
  };

  const handleReviewFilterChange = (newFilter) => {
    setReviewFilter(newFilter);
    setVisibleReviewCount(3);
  };

  const handleStartReply = (review) => {
    setReplyingTo(review.id);
    setReplyText(review.providerReply || '');
    setReplyError(null);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
    setReplyError(null);
  };

  const handleSubmitReply = async (reviewId) => {
    if (!replyText.trim()) {
      setReplyError('A válasz szövege nem lehet üres.');
      return;
    }
    setReplyLoading(true);
    setReplyError(null);
    try {
      const response = await reviewService.replyToReview(reviewId, replyText.trim());
      if (response.success) {
        const now = new Date().toISOString();
        setReviews(prev =>
          prev.map(r =>
            r.id === reviewId
              ? { ...r, providerReply: replyText.trim(), providerRepliedAt: now }
              : r
          )
        );
        handleCancelReply();
      } else {
        setReplyError(response.message || 'Válasz elküldése sikertelen.');
      }
    } catch {
      setReplyError('Szerver hiba a válasz elküldése során.');
    } finally {
      setReplyLoading(false);
    }
  };

  const commonBoxStyles = {
    textAlign: 'center',
    width: '100%',
    maxWidth: { xs: '100%', md: 1440 },
    mx: 'auto',
  };

  if (loading) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        sx={{ py: 4, px: { xs: 1, sm: 2 } }}
      >
        <Box sx={commonBoxStyles}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Szolgáltató betöltése...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        sx={{ py: 4, px: { xs: 1, sm: 2 } }}
      >
        <Box sx={commonBoxStyles}>
          <Alert severity="error">{error}</Alert>
          <Button onClick={() => navigate('/szolgaltatok')} sx={{ mt: 2 }}>
            Vissza a szolgáltatókhoz
          </Button>
        </Box>
      </Container>
    );
  }

  if (!provider) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        sx={{ py: 4, px: { xs: 1, sm: 2 } }}
      >
        <Box sx={commonBoxStyles}>
          <Typography variant="h6">Szolgáltató nem található</Typography>
          <Button onClick={() => navigate('/szolgaltatok')} sx={{ mt: 2 }}>
            Vissza a szolgáltatókhoz
          </Button>
        </Box>
      </Container>
    );
  }
  
  const approvedReviews = reviews.filter(review => review.status === 'APPROVED');
  const isOwnProfile = isAuthenticated && isProvider() && provider?.userId === user?.id;
  const goodReviewCount = approvedReviews.filter(r => r.rating >= 4).length;
  const badReviewCount = approvedReviews.filter(r => r.rating <= 3).length;
  const filteredApprovedReviews = approvedReviews.filter(r => {
    if (reviewFilter === 'good') return r.rating >= 4;
    if (reviewFilter === 'bad') return r.rating <= 3;
    return true;
  });
  const visibleApprovedReviews = filteredApprovedReviews.slice(0, visibleReviewCount);
  const hasMoreApprovedReviews = filteredApprovedReviews.length > visibleReviewCount;


  const pageTitle = provider
    ? `${provider.businessName}${provider.city ? ` – ${provider.city}` : ''} | Wedigo`
    : 'Szolgáltató | Wedigo';
  const pageDescription = provider
    ? `${provider.businessName}${provider.category?.name ? ` – ${provider.category.name}` : ''}${provider.city ? `, ${provider.city}` : ''}. ${provider.description ? provider.description.slice(0, 120) + '…' : 'Esküvői szolgáltató a Wedigo piacterén.'}`
    : 'Esküvői szolgáltató a Wedigo piacterén.';
  const pageImage = provider?.user?.avatarUrl
    || provider?.images?.[0]?.imageUrl
    || null;

  // Morzsamenü a landing oldalakra: Szolgáltatók › fő kategória › fő kategória + első megye (belső linkelés, SEO)
  const mainCategory = provider?.providerCategories?.[0]?.category || provider?.category || null;
  const mainCounty = primaryCounty(provider?.county);
  const breadcrumbs = provider
    ? [
        { name: 'Szolgáltatók', path: '/szolgaltatok' },
        ...(mainCategory ? [{ name: mainCategory.name, path: categoryLandingPath(mainCategory) }] : []),
        ...(mainCategory && mainCounty
          ? [{ name: countyLabel(mainCounty), path: providersLandingPath({ categorySlug: mainCategory.slug, county: mainCounty }) }]
          : []),
      ]
    : [];

  return (
    // MÓDOSÍTVA: Fix háttér wrapper Box
    <>
    {provider && (
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`https://wedigo.hu/szolgaltato/${provider.slug || provider.id}`} />
        {pageImage && <meta property="og:image" content={pageImage} />}
        <link rel="canonical" href={`https://wedigo.hu/szolgaltato/${provider.slug || provider.id}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": provider.businessName,
          "description": provider.description || undefined,
          "address": provider.city ? { "@type": "PostalAddress", "addressLocality": provider.city, "addressCountry": "HU" } : undefined,
          "aggregateRating": provider.reviewCount > 0 ? {
            "@type": "AggregateRating",
            "ratingValue": parseFloat(provider.ratingAverage).toFixed(1),
            "reviewCount": provider.reviewCount
          } : undefined,
          "url": `https://wedigo.hu/szolgaltato/${provider.slug || provider.id}`
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            ...breadcrumbs,
            { name: provider.businessName, path: `/szolgaltato/${provider.slug || provider.id}` },
          ].map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": `https://wedigo.hu${item.path}`,
          })),
        })}</script>
      </Helmet>
    )}
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: {
            xs: LEAVES_BG_XS,
            md: LEAVES_BG_MD
          },
          backgroundSize: {
            xs: '100% auto',
            md: 'cover'
          },
          backgroundPosition: {
            xs: 'center top',
            md: 'center center'
          },
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
          willChange: 'transform',
          transform: 'translateZ(0)',
        }
      }}
    >
      <Container
        maxWidth="lg" // MÓDOSÍTVA: lg helyett false-ról
        sx={{
          py: 4,
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Preview banner */}
        {slug === '__preview__' && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <strong>Előnézet mód</strong> – Ez az oldal az aktuálisan szerkesztett (nem feltétlenül mentett) adatok alapján mutatja meg a profilodat. Értékelések és interaktív funkciók itt nem érhetők el.
            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => window.close()}>
                Előnézet bezárása
              </Button>
            </Box>
          </Alert>
        )}

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Vissza gomb és morzsamenü egy sorban, függőlegesen középre igazítva */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mb: 3 }}>
        {slug !== '__preview__' && (
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/szolgaltatok')}
          sx={{
            borderRadius: '12px',
            px: 2,
            flexShrink: 0,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateX(-4px)',
              boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
            }
          }}
        >
          Vissza a szolgáltatókhoz
        </Button>
        )}

        {breadcrumbs.length > 0 && (
          <Breadcrumbs
            aria-label="Morzsamenü"
            sx={{
              display: 'inline-flex',
              minWidth: 0,
              maxWidth: '100%',
              px: 2,
              py: 0.75,
              borderRadius: '20px',
              bgcolor: alpha(theme.palette.background.paper, 0.85),
              fontSize: '0.875rem',
              '& .MuiBreadcrumbs-separator': { mx: 0.75 },
              // Mobilon a szolgáltató neve (amúgy is a <h1>) és az előtte álló elválasztó kimarad
              '& .MuiBreadcrumbs-li:last-of-type, & .MuiBreadcrumbs-separator:nth-last-of-type(2)': {
                display: { xs: 'none', sm: 'flex' },
              },
            }}
          >
            {breadcrumbs.map((item) => (
              <Link
                key={item.path}
                component={RouterLink}
                to={item.path}
                underline="hover"
                sx={{ color: 'primary.dark', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                {item.name}
              </Link>
            ))}
            <Typography
              sx={{ fontSize: 'inherit', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}
              aria-current="page"
            >
              {provider.businessName}
            </Typography>
          </Breadcrumbs>
        )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: { xs: 'stretch', md: 'flex-start' } }}>
          {/* Bal oldal - Fő információk */}
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            {/* MÓDOSÍTVA: Fejléc kártya - Lekerekítés és árnyék */}
            <Card 
              sx={{ 
                mb: 3,
                borderRadius: '20px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                transition: 'box-shadow 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: { xs: 1.5, sm: 2 } }}>
                    <Avatar
                      src={provider.user?.avatarUrl || undefined}
                      sx={{
                        width: { xs: 56, sm: 72, md: 96 },
                        height: { xs: 56, sm: 72, md: 96 },
                        flexShrink: 0,
                        bgcolor: 'primary.main',
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        border: `3px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                      }}
                    >
                      {!provider.user?.avatarUrl && provider.businessName?.charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        component="h1"
                        sx={{
                          fontFamily: 'Playfair Display',
                          fontWeight: 700,
                          color: theme.palette.primary.dark,
                          mb: 0.5,
                          fontSize: { xs: '1.25rem', sm: '1.6rem', md: '2rem' },
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: { xs: 'nowrap', sm: 'normal' },
                        }}
                      >
                        {provider.businessName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Rating
                          value={parseFloat(provider.ratingAverage) || 0}
                          precision={0.1}
                          readOnly
                          size="small"
                          sx={{ '& .MuiRating-iconFilled': { color: theme.palette.secondary.main } }}
                        />
                        <Typography variant="body2" sx={{ color: theme.palette.primary.dark }}>
                          {parseFloat(provider.ratingAverage).toFixed(1)} ({provider.reviewCount})
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <FavoriteButton
                      providerId={provider.id}
                      size="small"
                      showTooltip={true}
                    />
                    <IconButton
                      onClick={handleShare}
                      size="small"
                      sx={{
                        borderRadius: '10px',
                        border: `2px solid ${theme.palette.divider}`,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      <Share fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Chipek - MÓDOSÍTVA: Jobb stílus */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                  {(provider.providerCategories?.length > 0 || provider.category) && (
                    (provider.providerCategories?.length > 0
                      ? provider.providerCategories.map(pc => pc.category)
                      : [provider.category]
                    ).map(cat => (
                      <Chip
                        key={cat.id}
                        label={cat.name}
                        component={RouterLink}
                        to={categoryLandingPath(cat)}
                        clickable
                        sx={{
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.dark,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                          }
                        }}
                      />
                    ))
                  )}
                  {provider.city && (
                    <Chip 
                      icon={<LocationOn />}
                      label={provider.city}
                      variant="outlined"
                      sx={{ 
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.dark,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          borderColor: theme.palette.primary.main,
                          '& .MuiChip-icon': { color: 'white' }
                        }
                      }}
                      onClick={() => handleCityChipClick(provider.city)}
                    />
                  )}
                  {provider.status === 'APPROVED' && (
                    <Chip
                      icon={<Verified />}
                      label="Ellenőrzött"
                      color="success"
                      sx={{
                        fontWeight: 600,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 8px ${theme.palette.success.main}40`,
                        }
                      }}
                    />
                  )}
                </Box>

                {/* Ár */}
                {(provider.priceRangeMin > 0 || provider.priceRangeMax > 0) && (
                  <Typography variant="body2" sx={{ mb: 2, color: theme.palette.primary.dark, fontWeight: 600 }}>
                    Ár:{' '}
                    {provider.priceRangeMin > 0 && provider.priceRangeMax > 0
                      ? `${provider.priceRangeMin.toLocaleString('hu-HU')} – ${provider.priceRangeMax.toLocaleString('hu-HU')} Ft`
                      : provider.priceRangeMin > 0
                        ? `${provider.priceRangeMin.toLocaleString('hu-HU')} Ft-tól`
                        : `${provider.priceRangeMax.toLocaleString('hu-HU')} Ft-ig`
                    }
                  </Typography>
                )}

                {/* Rövid leírás */}
                {provider.shortDescription && (
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 3,
                      lineHeight: 1.7,
                      color: theme.palette.primary.dark,
                    }}
                  >
                    {provider.shortDescription}
                  </Typography>
                )}

              </CardContent>
            </Card>

            {/* Többi szekció (Galéria, Bemutatkozás, stb.) - MÓDOSÍTVA: Paper helyett Card + stílusok */}
            {provider.images && provider.images.length > 0 && (
              <Card
                sx={{
                  mb: 3,
                  borderRadius: '20px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 3, pt: 3, pb: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: 'Playfair Display',
                      fontWeight: 700,
                      color: theme.palette.primary.dark,
                    }}
                  >
                    Munkáim
                  </Typography>
                </Box>
                <Box sx={{ px: 3, pb: 3 }}>
                  <ImageGallery
                    images={provider.images}
                    providerName={provider.businessName}
                    providerId={provider.id}
                    mainImageAspectRatio="70%"
                    imageSx={{ objectFit: 'cover' }}
                    containerSx={{ borderRadius: '12px' }}
                    onImageClick={(index) => setLightboxIndex(index)}
                  />
                </Box>
              </Card>
            )}

            {/* Részletes bemutatkozás */}
            {provider.description && (
              <Card 
                sx={{ 
                  mb: 3,
                  borderRadius: '20px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{
                      fontFamily: 'Playfair Display',
                      fontWeight: 700,
                      color: theme.palette.primary.dark,
                      mb: 3,
                    }}
                  >
                    Részletes bemutatkozás
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-line',
                      lineHeight: 1.8,
                      color: theme.palette.primary.dark,
                    }}
                  >
                    {provider.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Csomagok szekció */}
            {provider?.packages?.length > 0 && (
            <Card
              sx={{
                mb: 3,
                borderRadius: '20px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontFamily: 'Playfair Display',
                    fontWeight: 700,
                    color: theme.palette.primary.dark,
                    mb: 3,
                  }}
                >
                  Szolgáltatási csomagok
                </Typography>
                <PackageList packages={provider.packages} />
              </CardContent>
            </Card>
            )}

                        {/* Értékelések szekció */}
            <Card
              ref={reviewsRef}
              sx={{
                mb: 3,
                borderRadius: '20px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                  <Typography 
                    variant="h5"
                    sx={{
                      fontFamily: 'Playfair Display',
                      fontWeight: 700,
                      color: theme.palette.primary.dark,
                    }}
                  >
                    Ügyfél értékelések ({approvedReviews.length})
                  </Typography>
                  {slug !== '__preview__' && (
                  <Button
                    variant="contained"
                    startIcon={<RateReview sx={{ color: '#ffffff !important' }} />}
                    onClick={() => setWriteReviewOpen(true)}
                    disabled={!canWriteReview()}
                    sx={{
                      borderRadius: '12px',
                      color: '#ffffff !important',
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}60`,
                      }
                    }}
                  >
                    {getReviewButtonText()}
                  </Button>
                  )}
                </Box>

                {/* Szűrő chipek */}
                {approvedReviews.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                    {[
                      { value: 'all', label: `Összes (${approvedReviews.length})`, icon: <FormatListBulleted sx={{ fontSize: 16 }} /> },
                      { value: 'good', label: `${goodReviewCount}`, icon: <ThumbUp sx={{ fontSize: 16 }} />, color: 'success' },
                      { value: 'bad', label: `${badReviewCount}`, icon: <ThumbDown sx={{ fontSize: 16 }} />, color: 'error' },
                    ].map(({ value, label, icon, color }) => (
                      <Chip
                        key={value}
                        label={label}
                        icon={icon}
                        onClick={() => handleReviewFilterChange(value)}
                        color={reviewFilter === value ? (color || 'primary') : 'default'}
                        variant={reviewFilter === value ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                )}

                {approvedReviews.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      textAlign: 'center',
                      py: 6,
                      borderRadius: '16px',
                      border: `2px dashed ${theme.palette.divider}`,
                      bgcolor: theme.palette.grey[50],
                    }}
                  >
                    <RateReview sx={{ fontSize: 64, color: theme.palette.grey[300], mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Még nincsenek értékelések
                    </Typography>
                    {canWriteReview() && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Legyen Ön az első, aki értékeli!
                      </Typography>
                    )}
                  </Paper>
                ) : filteredApprovedReviews.length === 0 ? (
                  <Alert severity="info">
                    {reviewFilter === 'good' ? 'Nincsenek jó értékelések.' : 'Nincsenek rossz értékelések.'}
                  </Alert>
                ) : (
                  <>
                    <Stack divider={<Divider />} spacing={0}>
                      {visibleApprovedReviews.map((review) => (
                        <Box
                          key={review.id}
                          id={`review-${review.id}`}
                          sx={{
                            py: 2,
                            px: 1,
                            borderRadius: '12px',
                            transition: 'background-color 0.3s',
                            bgcolor: String(review.id) === highlightedReviewId
                              ? alpha(theme.palette.warning.light, 0.3)
                              : 'transparent',
                            '&:hover': { bgcolor: String(review.id) === highlightedReviewId
                              ? alpha(theme.palette.warning.light, 0.3)
                              : theme.palette.grey[50] },
                          }}
                        >
                          {/* Fejléc sor: avatar + név + csillag + dátum */}
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Avatar
                              src={review.client?.avatarUrl || undefined}
                              sx={{
                                width: 48,
                                height: 48,
                                flexShrink: 0,
                                border: `2px solid ${theme.palette.secondary.main}`,
                                boxShadow: `0 2px 8px ${theme.palette.secondary.main}30`,
                              }}
                            >
                              {!review.client?.avatarUrl && review.client?.firstName?.charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {review.client?.firstName} {review.client?.lastName}
                                </Typography>
                                <Rating
                                  value={review.rating}
                                  size="small"
                                  readOnly
                                  sx={{ '& .MuiRating-iconFilled': { color: theme.palette.secondary.main } }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(review.createdAt).toLocaleDateString('hu-HU')}
                                </Typography>
                              </Box>
                              {review.comment && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    lineHeight: 1.6,
                                    color: theme.palette.text.primary,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {review.comment}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Szolgáltató válasza (meglévő, nem szerkesztés alatt) */}
                          {review.providerReply && replyingTo !== review.id && (
                            <Box
                              sx={{
                                ml: { xs: 0, sm: 8 },
                                mt: 1.5,
                                borderLeft: '3px solid',
                                borderColor: 'primary.main',
                                bgcolor: `${theme.palette.primary.main}08`,
                                borderRadius: '0 8px 8px 0',
                                p: 1.5,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                                  {provider.businessName} válasza:
                                </Typography>
                                {review.providerRepliedAt && (
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(review.providerRepliedAt).toLocaleDateString('hu-HU')}
                                  </Typography>
                                )}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  wordBreak: 'break-word',
                                  whiteSpace: 'pre-wrap',
                                  color: 'text.primary',
                                }}
                              >
                                {review.providerReply}
                              </Typography>
                              {isOwnProfile && (
                                <Button
                                  size="small"
                                  startIcon={<EditIcon />}
                                  onClick={() => handleStartReply(review)}
                                  sx={{ mt: 1, px: 0, minWidth: 0 }}
                                >
                                  Szerkesztés
                                </Button>
                              )}
                            </Box>
                          )}

                          {/* Válasz form (csak saját profil, inline) */}
                          {isOwnProfile && (
                            <Collapse in={replyingTo === review.id} unmountOnExit>
                              <Box sx={{ ml: { xs: 0, sm: 8 }, mt: 1.5 }}>
                                <TextField
                                  multiline
                                  minRows={3}
                                  maxRows={10}
                                  fullWidth
                                  label="Válasz az értékelésre"
                                  value={replyText}
                                  onChange={e => setReplyText(e.target.value)}
                                  error={!!replyError}
                                  helperText={replyError || `${replyText.length}/2000 karakter`}
                                  inputProps={{ maxLength: 2000 }}
                                  disabled={replyLoading}
                                  sx={{ mb: 1 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    onClick={handleCancelReply}
                                    disabled={replyLoading}
                                  >
                                    Mégse
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleSubmitReply(review.id)}
                                    disabled={replyLoading || !replyText.trim()}
                                    startIcon={replyLoading ? <CircularProgress size={14} color="inherit" /> : <ReplyIcon />}
                                  >
                                    {replyLoading ? 'Küldés...' : 'Küldés'}
                                  </Button>
                                </Box>
                              </Box>
                            </Collapse>
                          )}

                          {/* Válasz írása gomb (ha még nincs válasz és nem szerkesztés alatt) */}
                          {isOwnProfile && !review.providerReply && replyingTo !== review.id && (
                            <Box sx={{ ml: { xs: 0, sm: 8 }, mt: 1 }}>
                              <Button
                                size="small"
                                startIcon={<ReplyIcon />}
                                onClick={() => handleStartReply(review)}
                              >
                                Válasz írása
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Stack>

                    {hasMoreApprovedReviews && (
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setVisibleReviewCount(c => c + 3)}
                        sx={{
                          mt: 2,
                          borderRadius: '12px',
                          borderWidth: 2,
                          '&:hover': { borderWidth: 2 },
                        }}
                      >
                        Több megjelenítése ({filteredApprovedReviews.length - visibleReviewCount} értékelés)
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

          </Box>

          {/* Jobb oldal - Kapcsolatfelvétel */}
          <Box sx={{
            width: { xs: '100%', md: '340px' },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: { md: 24 },
            alignSelf: { md: 'flex-start' },
          }}>
              {/* MÓDOSÍTVA: Kapcsolatfelvétel kártya - Szebb stílus */}
              <Card 
                sx={{ 
                  mb: 3,
                  borderRadius: '20px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  color: 'white',
                  p: 3,
                  textAlign: 'center',
                }}>
                  <Typography 
                    variant="h5" 
                    component="div"
                    sx={{
                      fontFamily: 'Playfair Display',
                      fontWeight: 700,
                    }}
                  >
                    Kapcsolatfelvétel
                  </Typography>
                </Box>
                <CardContent sx={{ p: 3 }}>
                  {!isAuthenticated ? (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        A kapcsolati adatok megtekintéséhez jelentkezzen be.
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Phone />}
                        onClick={() => setLoginPromptOpen(true)}
                        sx={{
                          borderRadius: '10px',
                          borderWidth: 2,
                          '&:hover': { borderWidth: 2 },
                        }}
                      >
                        Bejelentkezés
                      </Button>
                    </Box>
                  ) : !contactRevealed ? (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Phone />}
                        onClick={() => {
                          setContactRevealed(true);
                          trackProviderEvent(ANALYTICS_EVENTS.CONTACT_REVEAL, provider.id);
                        }}
                        sx={{
                          borderRadius: '10px',
                          borderWidth: 2,
                          '&:hover': { borderWidth: 2 },
                        }}
                      >
                        Kapcsolati adatok megtekintése
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <List dense>
                        {provider.user?.phone && (
                          <ListItem
                            onClick={() => trackProviderEvent(ANALYTICS_EVENTS.CONTACT_LINK_CLICK, provider.id, { type: 'phone' })}
                            sx={{
                              mb: 1,
                              borderRadius: '12px',
                              transition: 'background-color 0.2s',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: theme.palette.grey[50] }
                            }}>
                            <Phone sx={{ color: theme.palette.primary.main, mr: 2 }} />
                            <ListItemText
                              primary={provider.user.phone}
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                          </ListItem>
                        )}
                        {(provider.contactEmail || provider.user?.email) && (
                          <ListItem
                            onClick={() => trackProviderEvent(ANALYTICS_EVENTS.CONTACT_LINK_CLICK, provider.id, { type: 'email' })}
                            sx={{
                              mb: 1,
                              borderRadius: '12px',
                              transition: 'background-color 0.2s',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: theme.palette.grey[50] }
                            }}>
                            <Email sx={{ color: theme.palette.primary.main, mr: 2 }} />
                            <ListItemText
                              primary={provider.contactEmail || provider.user?.email}
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                          </ListItem>
                        )}
                        {provider.address && (
                          <ListItem
                            onClick={() => trackProviderEvent(ANALYTICS_EVENTS.CONTACT_LINK_CLICK, provider.id, { type: 'address' })}
                            sx={{
                              borderRadius: '12px',
                              transition: 'background-color 0.2s',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: theme.palette.grey[50] }
                            }}>
                            <LocationOn sx={{ color: theme.palette.primary.main, mr: 2 }} />
                            <ListItemText
                              primary={provider.address}
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                          </ListItem>
                        )}
                        {!provider.user?.phone && !provider.user?.email && !provider.address && (
                          <ListItem>
                            <ListItemText
                              primary="Nincs megadva"
                              primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                            />
                          </ListItem>
                        )}
                      </List>
                      <Alert
                        severity="info"
                        sx={{ mt: 1, borderRadius: '10px', fontSize: '0.82rem', py: 0.5 }}
                      >
                        Ha felveszi a kapcsolatot, kérjük, említse meg, hogy a <strong>Wedigo</strong> oldalán talált rá a szolgáltatóra!
                      </Alert>
                    </>
                  )}

                  {(provider.website || provider.facebookUrl || provider.instagramUrl || provider.tiktokUrl) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', px: 1 }}>
                        {provider.website && (
                          <IconButton
                            component="a"
                            href={ensureAbsoluteUrl(provider.website)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Weboldal"
                            onClick={() => trackProviderEvent(ANALYTICS_EVENTS.SOCIAL_CLICK, provider.id, { platform: 'website' })}
                            sx={{
                              color: theme.palette.primary.main,
                              p: 0.75,
                              '&:hover': { bgcolor: theme.palette.action.hover }
                            }}
                          >
                            <WebsiteIcon size={24} color={theme.palette.primary.main} />
                          </IconButton>
                        )}
                        {provider.facebookUrl && (
                          <IconButton
                            component="a"
                            href={ensureAbsoluteUrl(provider.facebookUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Facebook"
                            onClick={() => trackProviderEvent(ANALYTICS_EVENTS.SOCIAL_CLICK, provider.id, { platform: 'facebook' })}
                            sx={{
                              p: 0.75,
                              '&:hover': { bgcolor: 'rgba(24, 119, 242, 0.1)' }
                            }}
                          >
                            <FacebookIcon size={24} />
                          </IconButton>
                        )}
                        {provider.instagramUrl && (
                          <IconButton
                            component="a"
                            href={ensureAbsoluteUrl(provider.instagramUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Instagram"
                            onClick={() => trackProviderEvent(ANALYTICS_EVENTS.SOCIAL_CLICK, provider.id, { platform: 'instagram' })}
                            sx={{
                              p: 0.75,
                              '&:hover': { bgcolor: 'rgba(224, 148, 51, 0.1)' }
                            }}
                          >
                            <InstagramIcon size={24} />
                          </IconButton>
                        )}
                        {provider.tiktokUrl && (
                          <IconButton
                            component="a"
                            href={ensureAbsoluteUrl(provider.tiktokUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="TikTok"
                            onClick={() => trackProviderEvent(ANALYTICS_EVENTS.SOCIAL_CLICK, provider.id, { platform: 'tiktok' })}
                            sx={{
                              p: 0.75,
                              '&:hover': { bgcolor: theme.palette.action.hover }
                            }}
                          >
                            <TikTokIcon size={24} />
                          </IconButton>
                        )}
                      </Box>
                    </>
                  )}

                  {slug !== '__preview__' && (
                  <>
                  <Divider sx={{ my: 3 }} />

                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Event />}
                    onClick={() => isAuthenticated ? setOfferRequestDialogOpen(true) : setLoginPromptOpen(true)}
                    sx={{
                      mb: 1.5,
                      borderRadius: '10px',
                      py: 1,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 12px ${theme.palette.primary.main}50`,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 16px ${theme.palette.primary.main}70`,
                        background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      },
                    }}
                  >
                    Ajánlatkérés
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    startIcon={<Message />}
                    onClick={() => isAuthenticated ? setContactDialogOpen(true) : setLoginPromptOpen(true)}
                    sx={{
                      borderRadius: '10px',
                      py: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 16px ${theme.palette.secondary.main}70`,
                      },
                    }}
                  >
                    Üzenet küldése
                  </Button>
                  </>
                  )}
                </CardContent>
              </Card>

              {/* Térkép szekció - MÓDOSÍTVA */}
              {mapCenter && (
                <Card 
                  sx={{ 
                    borderRadius: '20px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography 
                        variant="h5"
                        sx={{
                          fontFamily: 'Playfair Display',
                          fontWeight: 700,
                          color: theme.palette.primary.dark,
                        }}
                      >
                        Helyszín
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ZoomIn />}
                        onClick={() => setMapZoomDialogOpen(true)}
                        sx={{
                          borderRadius: '12px',
                          borderWidth: 2,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderWidth: 2,
                            transform: 'scale(1.05)',
                            boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
                          }
                        }}
                      >
                        Nagyítás
                      </Button>
                    </Box>
                    <Box sx={{ 
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: `2px solid ${theme.palette.divider}`,
                    }}>
                      <MapComponent
                        center={mapCenter}
                        markers={mapMarkers}
                        zoom={12}
                        containerStyle={{ width: '100%', height: '300px' }}
                      />
                    </Box>
                    {nearbyProviders.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography 
                          variant="subtitle1" 
                          gutterBottom
                          sx={{ 
                            fontWeight: 600,
                            color: theme.palette.primary.dark,
                          }}
                        >
                          Közeli szolgáltatók ({Math.min(nearbyProviders.length, 3)}):
                        </Typography>
                        <List dense>
                          {nearbyProviders.slice(0, 3).map((p) => (
                            <ListItem 
                              key={p.id} 
                              disablePadding
                              sx={{
                                mb: 1,
                                borderRadius: '8px',
                                transition: 'background-color 0.2s',
                                '&:hover': { bgcolor: theme.palette.grey[50] }
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate(`/szolgaltato/${p.slug || p.id}`)}
                                    sx={{
                                      fontWeight: 600,
                                      color: theme.palette.primary.main,
                                      textDecoration: 'none',
                                      textAlign: 'left',
                                      display: 'block',
                                      width: '100%',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      }
                                    }}
                                  >
                                    {p.businessName} ({p.distance.toFixed(1)} km)
                                  </Link>
                                }
                                secondary={`${p.city}, ${p.county}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
          </Box>
        </Box>
      </Container>
                    
      {/* ... (Kapcsolatfelvételi dialog, Kontakt infó másolós Dialog, Értékelés írása dialog, OfferRequestForm Dialog, Snackbar, Térkép nagyítás dialógus) ... */}
      {/* Kapcsolatfelvételi dialog (Üzenet küldése) */}
      <Dialog 
        open={contactDialogOpen} 
        onClose={() => setContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Üzenet küldése - {provider.businessName}
        </DialogTitle>
        <DialogContent>
          {submitStatus && (
            <Alert 
              severity={submitStatus.type === 'error' ? 'error' : submitStatus.type === 'success' ? 'success' : 'info'} 
              sx={{ mb: 2 }}
            >
              {submitStatus.message}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Név"
            margin="normal"
            value={contactForm.name}
            onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
            disabled={isAuthenticated}
          />
          <TextField
            fullWidth
            label="Email cím"
            type="email"
            margin="normal"
            value={contactForm.email}
            onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
            disabled={isAuthenticated}
          />
          <TextField
            fullWidth
            label="Telefonszám"
            margin="normal"
            value={contactForm.phone}
            onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
            disabled={isAuthenticated}
          />
          <TextField
            fullWidth
            label="Esemény dátuma (opcionális)"
            type="date"
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={contactForm.eventDate}
            onChange={(e) => setContactForm({...contactForm, eventDate: e.target.value})}
          />
          <TextField
            fullWidth
            label="Üzenet"
            multiline
            rows={4}
            margin="normal"
            placeholder="Írja le részletesen az igényeit..."
            value={contactForm.message}
            onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>
            Mégse
          </Button>
          <Button 
            variant="contained" 
            onClick={handleContactSubmit}
            disabled={submitStatus?.type === 'loading' || !isAuthenticated}
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 10px ${theme.palette.primary.main}80`,
              }
            }}
          >
            {submitStatus?.type === 'loading' ? 'Küldés...' : 'Üzenet küldése'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kontakt infó másolós Dialog */}
      <Dialog 
        open={contactInfoDialogOpen} 
        onClose={() => setContactInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #E8E8E8'
        }}>
          <Typography variant="h6">
            {contactType === 'phone' ? 'Telefonszám' : 'Email cím'}
          </Typography>
          <IconButton onClick={() => setContactInfoDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            p: 2,
            backgroundColor: '#F5F5F5',
            borderRadius: '12px',
            mb: 2
          }}>
            {contactType === 'phone' ? <Phone /> : <Email />}
            <Typography variant="h6" sx={{ flex: 1 }}>
              {contactType === 'phone' 
                ? provider?.user?.phone 
                : provider?.user?.email
              }
            </Typography>
            <Button
              variant="contained"
              startIcon={<ContentCopy />}
              onClick={() => handleCopyToClipboard(
                contactType === 'phone' 
                  ? provider?.user?.phone 
                  : provider?.user?.email,
                contactType === 'phone' ? 'Telefonszám' : 'Email cím'
              )}
              sx={{ 
                borderRadius: '8px',
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 10px ${theme.palette.primary.main}80`,
                }
              }}
            >
              Másolás
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {contactType === 'phone' 
              ? 'Kattintson a másolás gombra a telefonszám vágólapra másolásához.'
              : 'Kattintson a másolás gombra az email cím vágólapra másolásához.'
            }
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Képnézegető lightbox */}
      <ImageLightbox
        open={lightboxIndex !== null}
        images={provider.images || []}
        startIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
        providerId={provider.id}
        sessionId={sessionIdRef.current}
      />

      {/* Értékelés írása dialog */}
      <WriteReview
        open={writeReviewOpen}
        onClose={() => setWriteReviewOpen(false)}
        providerId={provider.id}
        onReviewSubmitted={handleReviewSubmitted}
      />

      {/* OfferRequestForm Dialog */}
      <OfferRequestForm
        open={offerRequestDialogOpen}
        onClose={() => setOfferRequestDialogOpen(false)}
        provider={provider}
        onSuccess={handleOfferRequestSuccess}
      />

      {/* Login szükséges dialógus (ajánlatkéréshez) */}
      <Dialog
        open={loginPromptOpen}
        onClose={() => setLoginPromptOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: 'Playfair Display', fontWeight: 700 }}>
          Bejelentkezés szükséges
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            A folytatáshoz be kell jelentkeznie. Kérjük, jelentkezzen be vagy regisztráljon!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setLoginPromptOpen(false)}
            variant="outlined"
            sx={{ borderRadius: '10px', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
          >
            Mégse
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/login', { state: { from: location.pathname } })}
            sx={{ borderRadius: '10px' }}
          >
            Bejelentkezés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar a visszajelzésekhez */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setSnackbarOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Térkép nagyítás dialógus */}
      <Dialog
        open={mapZoomDialogOpen}
        onClose={() => setMapZoomDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            height: '85vh',
            position: 'relative',
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        {/* Lebegő bezáró gomb a térkép felett */}
        <IconButton
          onClick={() => setMapZoomDialogOpen(false)}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
            bgcolor: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(4px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            '&:hover': { bgcolor: 'white', transform: 'scale(1.1)' },
            transition: 'all 0.2s',
          }}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {mapCenter && (
            <MapComponent
              center={mapCenter}
              markers={mapMarkers}
              zoom={14}
              containerStyle={{ width: '100%', height: '100%' }}
              highlightMarkerId={provider.id}
            />
          )}
        </DialogContent>
      </Dialog>
   </Box>
    </>
  );
};

export default ServiceProviderDetail;